import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import type { FreightHandlingUnitRow } from "@redotech/redo-fulfillment-common/freight/freight-handling-unit";
import {
  convertWeight,
  roundWeight,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import {
  RedoTable,
  RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { AssignProductsModal } from "./assign-products-modal";
import {
  AssignedProduct,
  createHandlingUnit,
  FreightProduct,
  FreightStepData,
  HandlingUnit,
} from "./freight-booking-types";
import * as styles from "./freight-step.module.css";
import { HandlingUnitCard } from "./handling-unit-card";

interface FreightStepProps {
  data: FreightStepData;
  setData: (data: FreightStepData) => void;
  savedHandlingUnits: FreightHandlingUnitRow[];
}

export const FreightStep = memo(function FreightStep({
  data,
  setData,
  savedHandlingUnits,
}: FreightStepProps) {
  const defaultSavedUnit = useMemo(
    () => savedHandlingUnits.find((u) => u.isDefault),
    [savedHandlingUnits],
  );

  const addHandlingUnit = useCallback(() => {
    setData({
      ...data,
      handlingUnits: [
        ...data.handlingUnits,
        createHandlingUnit(defaultSavedUnit),
      ],
    });
  }, [data, setData, defaultSavedUnit]);

  const updateHandlingUnit = useCallback(
    (index: number, updates: Partial<HandlingUnit>) => {
      const newUnits = [...data.handlingUnits];
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newUnits[index] = { ...newUnits[index]!, ...updates };
      setData({ ...data, handlingUnits: newUnits });
    },
    [data, setData],
  );

  const deleteHandlingUnit = useCallback(
    (index: number) => {
      const newUnits = data.handlingUnits.filter((_, i) => i !== index);
      setData({ ...data, handlingUnits: newUnits });
    },
    [data, setData],
  );

  const [assignModalUnitIndex, setAssignModalUnitIndex] = useState<
    number | undefined
  >(undefined);

  const handleAssignProducts = useCallback((unitIndex: number) => {
    setAssignModalUnitIndex(unitIndex);
  }, []);

  const handleCloseAssignModal = useCallback(() => {
    setAssignModalUnitIndex(undefined);
  }, []);

  const handleConfirmAssign = useCallback(
    (assignedProducts: AssignedProduct[]) => {
      if (assignModalUnitIndex === undefined) {
        return;
      }
      updateHandlingUnit(assignModalUnitIndex, { assignedProducts });
      setAssignModalUnitIndex(undefined);
    },
    [assignModalUnitIndex, updateHandlingUnit],
  );

  const updateProductWeight = useCallback(
    (productId: string, newWeightLbs: number) => {
      const newProducts = data.products.map((p) =>
        p.id === productId
          ? { ...p, itemWeight: { unit: WeightUnit.Lb, value: newWeightLbs } }
          : p,
      );
      setData({ ...data, products: newProducts });
    },
    [data, setData],
  );

  const assignedPerProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const unit of data.handlingUnits) {
      for (const ap of unit.assignedProducts) {
        map.set(ap.productId, (map.get(ap.productId) ?? 0) + ap.quantity);
      }
    }
    return map;
  }, [data.handlingUnits]);

  const totals = useMemo(() => {
    let quantity = 0;
    let weight = 0;
    let unassigned = 0;
    for (const p of data.products) {
      quantity += p.quantity;
      weight += convertWeight(p.itemWeight, WeightUnit.Lb).value * p.quantity;
      unassigned += p.quantity - (assignedPerProduct.get(p.id) ?? 0);
    }
    return { quantity, weight, unassigned };
  }, [data.products, assignedPerProduct]);

  interface FreightSummary {
    quantity: number;
    unassigned: number;
    weight: string;
  }

  const freightSummary: FreightSummary = useMemo(
    () => ({
      quantity: totals.quantity,
      unassigned: totals.unassigned,
      weight: `${roundWeight(totals.weight)} lbs`,
    }),
    [totals],
  );

  const redoTableColumns: RedoTableColumn<FreightProduct, FreightSummary>[] =
    useMemo(
      () => [
        {
          widthMode: { type: "fill" as const, relativeLength: 7 },
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="secondary">
                Product
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="xs">
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  {row.name}
                  {row.sku ? " - " : ""}
                </Text>
                <Text fontSize="xs" fontWeight="regular" textColor="secondary">
                  {row.sku ? row.sku : ""}
                </Text>
              </Flex>
            </RedoTablePaddedCell>
          ),
          renderSummaryCell: () => (
            <RedoTableTextCell fontWeight="semibold" text="Total" />
          ),
        },
        {
          widthMode: { type: "fill" as const },
          alignment: "center" as const,
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="secondary">
                Quantity
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={String(row.quantity)} />
          ),
          renderSummaryCell: ({ summary }) => (
            <RedoTableTextCell
              fontWeight="semibold"
              text={String(summary.quantity)}
            />
          ),
        },
        {
          widthMode: { type: "fill" as const },
          alignment: "center" as const,
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="secondary">
                Unassigned
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => {
            const assigned = assignedPerProduct.get(row.id) ?? 0;
            return <RedoTableTextCell text={String(row.quantity - assigned)} />;
          },
          renderSummaryCell: ({ summary }) => (
            <RedoTableTextCell
              fontWeight="medium"
              text={String(summary.unassigned)}
              textColor="primary"
            />
          ),
        },
        {
          widthMode: { type: "fill" as const, relativeLength: 3 },
          alignment: "center" as const,
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="secondary">
                Item weight (lbs)
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <RedoIncrementDecrement
                decimalPlaces={2}
                min={0}
                setValue={(val) => updateProductWeight(row.id, val)}
                size="small"
                step={0.1}
                suffix="lbs"
                value={roundWeight(
                  convertWeight(row.itemWeight, WeightUnit.Lb).value,
                )}
              />
            </RedoTablePaddedCell>
          ),
          renderSummaryCell: ({ summary }) => (
            <RedoTableTextCell fontWeight="semibold" text={summary.weight} />
          ),
        },
      ],
      [assignedPerProduct, updateProductWeight],
    );

  if (data.products.length === 0) {
    return (
      <Flex align="center" grow={1} justify="center" p="3xl">
        <Text fontSize="sm" textColor="secondary">
          No products available for freight booking.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.container} dir="column" gap="xl" mx="auto" p="3xl">
      <Text fontSize="md" fontWeight="semibold" textColor="primary">
        How are you shipping these items?
      </Text>
      <div className={styles.tableWrapper}>
        <RedoTable<FreightProduct, FreightSummary>
          additionalClasses={styles.table}
          layout={{ columns: redoTableColumns }}
          rowsOfData={data.products}
          size="sm"
          summary={{ data: freightSummary, position: "bottom" }}
        />
      </div>
      <Flex dir="column" gap="xl">
        <Flex align="center" justify="space-between">
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            Handling units
          </Text>
          <RedoButton
            hierarchy="primary"
            onClick={addHandlingUnit}
            size="sm"
            text="+ Add new handling unit"
          />
        </Flex>
        {data.handlingUnits.map((unit, index) => (
          <HandlingUnitCard
            handlingUnits={data.handlingUnits}
            key={index}
            onAssignProducts={handleAssignProducts}
            onDelete={deleteHandlingUnit}
            onUpdate={updateHandlingUnit}
            products={data.products}
            savedHandlingUnits={savedHandlingUnits}
            unit={unit}
            unitIndex={index}
          />
        ))}
      </Flex>
      {data.handlingUnits.length > 0 && <FreightSummarySection data={data} />}
      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Additional information
        </Text>
        <Flex gap="lg">
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="Customer reference 1 (optional)"
              maxLength={20}
              placeholder="Your reference"
              setValue={(val) => setData({ ...data, customerReference1: val })}
              size="sm"
              value={data.customerReference1}
            />
          </Flex>
          <Flex grow={1}>
            <RedoTextInput
              fullWidth
              label="Customer reference 2 (optional)"
              placeholder="Your reference"
              setValue={(val) => setData({ ...data, customerReference2: val })}
              size="sm"
              value={data.customerReference2}
            />
          </Flex>
        </Flex>
        <RedoTextareaInputField
          containerHeight="100px"
          label="Special instructions (optional)"
          placeholder="Enter special instructions here..."
          setValue={(val) => setData({ ...data, specialInstructions: val })}
          value={data.specialInstructions}
        />
      </Flex>
      {assignModalUnitIndex !== undefined && (
        <AssignProductsModal
          handlingUnits={data.handlingUnits}
          isOpen
          onAssign={handleConfirmAssign}
          onClose={handleCloseAssignModal}
          products={data.products}
          unitIndex={assignModalUnitIndex}
        />
      )}
    </Flex>
  );
});

const FreightSummarySection = memo(function FreightSummarySection({
  data,
}: {
  data: FreightStepData;
}) {
  const totalWeight = useMemo(() => {
    let weight = 0;
    for (const p of data.products) {
      weight += convertWeight(p.itemWeight, WeightUnit.Lb).value * p.quantity;
    }
    return weight;
  }, [data.products]);

  const totalHandlingUnits = useMemo(() => {
    let count = 0;
    for (const unit of data.handlingUnits) {
      count += unit.quantity;
    }
    return count;
  }, [data.handlingUnits]);

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="md" fontWeight="semibold" textColor="primary">
        Summary
      </Text>
      <Flex gap="3xl">
        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Total weight
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {roundWeight(totalWeight)} lbs
          </Text>
        </Flex>
        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Total handling units
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {totalHandlingUnits}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});
